import React from 'react';

/*
 * SubtypeExperiment
 * An original, conceptual illustration of the research setup — NOT a results
 * figure. It shows several "seen subtype" specimen cards and one related
 * held-out subtype, feeding a simple model/training stage under a fixed image
 * budget, with an arrow toward a correct broader-category prediction. All
 * shapes are original abstract leaf and bird silhouettes; no copyrighted
 * figures, no invented metrics or charts. Decorative (aria-hidden).
 */

/* an original abstract leaf silhouette, centred on the origin */
function Leaf({ x, y, fill, scale = 1 }) {
  return (
    <g transform={`translate(${x} ${y}) scale(${scale})`}>
      <path
        d="M0 -15 C 9 -9 9 9 0 15 C -9 9 -9 -9 0 -15 Z"
        fill={fill}
        stroke="#38443e"
        strokeWidth="1.1"
      />
      <path d="M0 -12 L0 12" stroke="#38443e" strokeWidth="0.9" opacity="0.5" />
    </g>
  );
}

/* an original abstract little bird silhouette, facing left */
function Bird({ x, y, fill, scale = 1 }) {
  return (
    <g transform={`translate(${x} ${y}) scale(${scale})`}>
      <path d="M8 6 q4 8 12 6" fill="none" stroke="#38443e" strokeWidth="1.1" />
      <ellipse cx="0" cy="0" rx="13" ry="9" fill={fill} stroke="#38443e" strokeWidth="1.1" />
      <circle cx="-9" cy="-4" r="5.2" fill={fill} stroke="#38443e" strokeWidth="1.1" />
      <path d="M-13 -4 l-5 1 l5 2 z" fill="#d6a65e" stroke="#b9863c" strokeWidth="0.8" />
      <circle cx="-10" cy="-5" r="1.2" fill="#38443e" />
      <path d="M6 -3 q7 -1 10 3 q-5 3 -10 1 z" fill="#38443e" opacity="0.14" />
    </g>
  );
}

function SubtypeExperiment({ className = '' }) {
  return (
    <svg
      className={className}
      viewBox="0 0 300 430"
      fill="none"
      role="img"
      aria-label="Concept diagram: several seen subtypes and one held-out subtype feed a model under a fixed image budget, which predicts the correct broader category."
      focusable="false"
    >
      {/* --- Seen subtypes --- */}
      <text x="22" y="15" className="se-label">SEEN SUBTYPES</text>
      <g>
        <rect x="22" y="22" width="78" height="62" rx="9" fill="#fffdf8" stroke="#cdd7c2" strokeWidth="1.4" />
        <Leaf x={61} y={53} fill="#a8be9a" />
        <rect x="111" y="22" width="78" height="62" rx="9" fill="#fffdf8" stroke="#cdd7c2" strokeWidth="1.4" />
        <Bird x={150} y={54} fill="#a9c7d8" />
        <rect x="200" y="22" width="78" height="62" rx="9" fill="#fffdf8" stroke="#cdd7c2" strokeWidth="1.4" />
        <Leaf x={239} y={53} fill="#d6a65e" scale={1.05} />
      </g>

      {/* --- Fixed image budget --- */}
      <rect x="86" y="96" width="128" height="22" rx="11" fill="rgba(168,190,154,0.18)" stroke="#7d9a73" strokeWidth="1.2" />
      <text x="150" y="111" className="se-chip" textAnchor="middle">fixed image budget</text>

      {/* --- Held-out subtype (related, dashed) --- */}
      <text x="22" y="145" className="se-label">HELD-OUT SUBTYPE</text>
      <rect x="111" y="152" width="78" height="62" rx="9" fill="#fff9ef" stroke="#b9863c" strokeWidth="1.6" strokeDasharray="5 4" />
      <Leaf x={150} y={183} fill="#c8b8d8" scale={1.05} />

      {/* --- Flow into the model --- */}
      <path d="M150 216 L150 236" stroke="#7d9a73" strokeWidth="2" strokeLinecap="round" />
      <path d="M150 240 l-5 -7 h10 z" fill="#7d9a73" />

      {/* --- Model / training stage --- */}
      <rect x="95" y="244" width="110" height="42" rx="12" fill="#a8be9a" stroke="#61745e" strokeWidth="1.6" />
      <text x="150" y="270" className="se-model" textAnchor="middle">model</text>

      {/* --- Flow to prediction --- */}
      <path d="M150 288 L150 308" stroke="#7d9a73" strokeWidth="2" strokeLinecap="round" />
      <path d="M150 312 l-5 -7 h10 z" fill="#7d9a73" />

      {/* --- Broader-category prediction --- */}
      <rect x="34" y="316" width="232" height="96" rx="14" fill="#fffdf8" stroke="#a9c7d8" strokeWidth="1.6" />
      <text x="150" y="336" className="se-label" textAnchor="middle">BROADER-CATEGORY PREDICTION</text>
      {/* the held-out subtype resolved into a group of related specimens */}
      <rect x="70" y="346" width="160" height="52" rx="12" fill="rgba(169,199,216,0.16)" stroke="#a9c7d8" strokeWidth="1.2" strokeDasharray="4 4" />
      <Leaf x={104} y={372} fill="#a8be9a" scale={0.72} />
      <Leaf x={132} y={372} fill="#d6a65e" scale={0.72} />
      <Leaf x={160} y={372} fill="#c8b8d8" scale={0.72} />
      {/* small correct-category check */}
      <circle cx="196" cy="372" r="11" fill="rgba(125,154,115,0.2)" stroke="#61745e" strokeWidth="1.4" />
      <path d="M191 372 l3.5 4 l6 -8" fill="none" stroke="#4c6044" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

export default SubtypeExperiment;
